
import { IItemCache } from '../models/IItemCacheObserver';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { IItemCacheState } from '../store/reducers/itemCacheReducer';
import { IItemCacheDependencies, IItemCacheDemands, ItemCacheObserver } from '../models/itemCache/ItemCacheObserver';
import { excludeResolvedDemands, getItems } from './getItems';
import { registerItemDemands, removeItemDemands, recordLastItemAccesses } from './pruneItems';

import { polyfill } from '../utilities/symbol';

polyfill();

/**
 * Creates an action which, when dispatched, produces an asynchronous iterator
 * for each state of demanded items until the request is fully-resolved.
 *
 * NOTE: This is forced to return 'any' types until callers can use TypeScript 3.0
 * or are updated to include 'esnext.asynciterable' in their 'lib' directives.
 */
export function observeItemsUntilResolved<T>(readDataFromItemCache: (itemCache: IItemCache) => T): IItemCacheThunkAction<any> { // tslint:disable-line
  return async function* (dispatch: IItemCacheDispatch, getState: () => IItemCacheState): any { // tslint:disable-line:no-function-expression max-line-length no-any
    let previousDependencies: IItemCacheDependencies | undefined;
    let previousDemands: IItemCacheDemands | undefined;

    let result: T;

    const demandOwner = {};

    try {
      do {
        const itemCache = new ItemCacheObserver({
          state: getState(),
          dispatch: dispatch,
          previousDependencies: previousDependencies,
          previousDemands: previousDemands
        });

        try {
          if (itemCache.haveDependenciesChanged()) {
            // This will always be true on the first run.
            ({
              result
            } = itemCache.evaluate(readDataFromItemCache));

            yield result;
          }
        } finally {
          itemCache.dispose();
        }

        const demands = itemCache.getDemands();
        const dependencies = itemCache.getDependencies();

        const payload = dispatch(excludeResolvedDemands({
          items: demands
        }));

        dispatch(recordLastItemAccesses({
          items: dependencies
        }));

        if (Object.keys(payload.items).length === 0) {
          return;
        }

        dispatch(registerItemDemands({
          owner: demandOwner,
          // Register all items touched in the selector callback,
          // regardless of whether a demand is issued.
          items: dependencies
        }));

        await dispatch(getItems(payload));

        previousDemands = demands;
        previousDependencies = dependencies;
      } while (true); // tslint:disable-line:no-constant-condition
    } finally {
      dispatch(removeItemDemands({
        owner: demandOwner
      }));
    }
  };
}
